import { listClasses } from '@mui/material/List';
import { menuItemClasses } from '@mui/material/MenuItem';
import Popover, { type PopoverProps } from '@mui/material/Popover';
import { alpha, styled } from '@mui/material/styles';
import { type ReactNode, useState } from 'react';
import {
  type ElementRect,
  getArrowOffset,
  getPaperOffsetStyles,
  getPopoverOrigin,
  type PopoverArrowPlacement,
} from './custom-popover-utils.js';
import { useElementRect } from './use-element-rect.js';

const DEFAULT_ARROW_SIZE = 14;
const DEFAULT_ARROW_PLACEMENT: PopoverArrowPlacement = 'top-right';
const DEFAULT_PAPER_OFFSET: [number, number] = [8, 2];
const ARROW_TRANSLATE = '48%';

type ArrowProps = {
  size: number;
  placement: PopoverArrowPlacement;
  anchorRect: ElementRect;
  paperRect: ElementRect;
};

const Arrow = styled('span', {
  shouldForwardProp: (prop) =>
    prop !== 'size' && prop !== 'placement' && prop !== 'anchorRect' && prop !== 'paperRect',
})<ArrowProps>(({ theme, size, placement, anchorRect, paperRect }) => {
  const { offsetX, offsetY } = getArrowOffset(anchorRect, paperRect, size);
  const [side = ''] = placement.split('-');

  const bySide: Record<string, Record<string, string | number>> = {
    top: { top: 0, rotate: '135deg', translate: `0 -${ARROW_TRANSLATE}`, left: `${offsetX}px` },
    bottom: {
      bottom: 0,
      rotate: '-45deg',
      translate: `0 ${ARROW_TRANSLATE}`,
      left: `${offsetX}px`,
    },
    left: { left: 0, rotate: '45deg', translate: `-${ARROW_TRANSLATE} 0`, top: `${offsetY}px` },
    right: { right: 0, rotate: '-135deg', translate: `${ARROW_TRANSLATE} 0`, top: `${offsetY}px` },
  };

  return {
    width: size,
    height: size,
    position: 'absolute',
    borderBottomLeftRadius: size / 4,
    clipPath: 'polygon(0% 0%, 100% 100%, 0% 100%)',
    backgroundColor: theme.palette.background.paper,
    border: `solid 1px ${alpha(theme.palette.grey[500], 0.12)}`,
    ...bySide[side],
  };
});

export type CustomPopoverProps = Omit<PopoverProps, 'open' | 'anchorEl' | 'children'> & {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  arrowPlacement?: PopoverArrowPlacement;
  hideArrow?: boolean;
};

/**
 * The kit's menu surface: the little arrow points back at the button that
 * opened it, which is how every row action menu in the Minimal screens reads.
 */
export function CustomPopover({
  open,
  anchorEl,
  onClose,
  children,
  arrowPlacement = DEFAULT_ARROW_PLACEMENT,
  hideArrow = false,
  slotProps,
  ...other
}: CustomPopoverProps) {
  const [paperEl, setPaperEl] = useState<HTMLDivElement | null>(null);
  const paperRect = useElementRect(paperEl, 'popoverPaper', open);
  const anchorRect = useElementRect(anchorEl, 'anchor', open);

  const { anchorOrigin, transformOrigin } = getPopoverOrigin(arrowPlacement);
  const isArrowVisible = !hideArrow && !!paperRect && !!anchorRect;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      slotProps={{
        ...slotProps,
        paper: {
          ref: setPaperEl,
          sx: {
            ...getPaperOffsetStyles(arrowPlacement, DEFAULT_PAPER_OFFSET),
            overflow: 'inherit',
            [`& .${listClasses.root}`]: { minWidth: 160 },
            [`& .${menuItemClasses.root}`]: { gap: 1.5 },
          },
        },
      }}
      {...other}
    >
      {isArrowVisible && (
        <Arrow
          size={DEFAULT_ARROW_SIZE}
          placement={arrowPlacement}
          paperRect={paperRect}
          anchorRect={anchorRect}
        />
      )}
      {children}
    </Popover>
  );
}
